<template>
  <section ref="fsRoot" class="cm-root" :class="{ 'cm-root--fs': isFullscreen }">
    <v-card elevation="0" class="cm-board">
      <v-card-text class="cm-board-body">

        <!-- ÚNICO FILTRO: ÁREA. En pantalla completa solo queda el botón de salida: el resto de
             los controles se oculta para dejar el espacio a las tarjetas. -->
        <div class="cm-area-row">
          <v-autocomplete
            v-if="!isFullscreen"
            v-model="idArea"
            :items="areas"
            :disabled="loading"
            label="Área"
            item-value="idArea"
            item-text="nombre"
            hide-details
            clearable
            outlined
            dense
            class="cm-area-select"
          />
          <span v-if="ventanaTxt" class="cm-periodo" title="Rango de tiempo consultado">
            <v-icon size="14">mdi-calendar-range</v-icon>
            <b>{{ periodoLabel }}</b>
            <span class="cm-periodo-rango">{{ ventanaTxt }}</span>
          </span>
          <span v-if="!isFullscreen" class="cm-refresh" :title="`Actualización automática cada ${Math.round(pollMs / 60000)} min`">
            <v-icon size="15" :class="{ 'cm-reload-spin': loading }">mdi-autorenew</v-icon>
            {{ loading ? 'Actualizando…' : `Actualiza en ${restanteTxt}` }}
          </span>
          <button
            v-if="!isFullscreen"
            class="cm-reload-btn"
            :disabled="loading"
            title="Recargar"
            @click="recargarManual"
          >
            <v-icon size="18" :class="{ 'cm-reload-spin': loading }">mdi-refresh</v-icon>
          </button>
          <button v-if="gamemode && !isFullscreen" class="cm-btn" @click="abrirConfig">
            <v-icon size="16" class="mr-1">mdi-cog-outline</v-icon>
            <span class="cm-btn-label">Configurar</span>
          </button>
          <button class="cm-btn" :class="{ 'cm-btn--solo': isFullscreen }" @click="extenderPantalla">
            <v-icon size="16" class="mr-1">{{ fsIcon }}</v-icon>
            <span class="cm-btn-label">{{ fsLabel }}</span>
          </button>
        </div>

        <v-progress-linear v-if="loading" indeterminate color="primary" rounded class="mb-2" />

        <div v-if="loading && !grupos.length" class="cm-loading">
          <v-progress-circular :size="64" :width="5" color="primary" indeterminate />
          <p class="cm-loading-text">Cargando cambios de molde...</p>
        </div>

        <div v-else-if="error" class="cm-empty">No se pudo cargar la información.</div>

        <div v-else-if="!grupos.length" class="cm-empty">
          Sin cambios de molde pendientes en el periodo.
        </div>

        <!-- Una fila por área: encabezado con el nombre y debajo las máquinas. -->
        <template v-else>
          <section v-for="grupo in grupos" :key="'area-' + grupo.area" class="cm-area-grupo">
            <header class="cm-area-header">
              <span class="cm-area-nombre">{{ grupo.area }}</span>
              <!-- Los cuatro criterios de orden, para que se entienda por qué una tarjeta está
                   antes que otra (y qué se recorta cuando hay límite por área). -->
              <v-tooltip bottom max-width="330" content-class="cm-orden-tip">
                <template #activator="{ on, attrs }">
                  <v-icon size="15" class="cm-area-info" v-bind="attrs" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div class="cm-orden-tit">Orden de las máquinas</div>
                <ol class="cm-orden-lista">
                  <li>Los moldes <b>críticos</b> van siempre primero.</li>
                  <li>Después las que tienen <b>hora programada</b>; al final las que no la tienen.</li>
                  <li>Entre ellas, la del <b>cambio más próximo</b> a la izquierda.</li>
                  <li>A igual hora, primero la máquina <b>disponible</b>: la que trae una OT en
                    curso pasa después, porque el molde sigue montado.</li>
                </ol>
              </v-tooltip>
            </header>

            <div class="cm-area-cards">
              <CardCambioMolde
                v-for="item in grupo.cambios"
                :key="'cambio-' + item.idMaquinaIns + '-' + item.idTrabajoOrden"
                :item="item"
                :ahora="ahoraMs"
                @detalle="abrirDetalle(item)"
              />
            </div>
          </section>
        </template>

      </v-card-text>
    </v-card>

    <!-- Detalle: la misma tarjeta con el tiempo de retraso agregado. -->
    <v-dialog v-model="dialogDetalle" max-width="380" content-class="cm-detalle-dialog">
      <CardCambioMolde
        v-if="maquinaDetalle"
        :item="maquinaDetalle"
        detalle
        :ahora="ahoraMs"
        style="width:100%"
      />
    </v-dialog>

    <!-- Configuración del reporte (solo gamemode) -->
    <v-dialog v-model="dialogConfig" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Configuración del reporte</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <label class="cm-cfg-lbl">Rango de tiempo</label>
          <v-select
            v-model="periodoDraft"
            :items="periodoOpciones"
            item-text="text"
            item-value="value"
            dense outlined hide-details
          />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            <b>Semana</b> toma desde el arranque de la jornada semanal y siete días más.
            <b>Día</b> se queda con la jornada en curso: de la hora de arranque a esa misma hora
            del día siguiente. Con <b>Día</b> solo aparecen los cambios de molde de hoy.
          </p>

          <v-divider class="my-4" />
          <v-checkbox
            v-model="limitarDraft"
            hide-details
            class="mt-0"
            label="Limitar tarjetas por área"
          />
          <div class="d-flex align-center mt-3" style="gap:8px">
            <span style="font-size:12px;color:#5A6476">Mostrar como máximo</span>
            <v-text-field
              v-model.number="limiteDraft"
              :disabled="!limitarDraft"
              type="number" min="1" max="50" step="1"
              dense outlined hide-details suffix="tarjetas"
              style="max-width:150px"
            />
          </div>
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Recorta cada área a los primeros N cambios, que por el orden son los críticos y los
            más próximos. Si se desactiva, se muestran todos.
          </p>

          <v-divider class="my-4" />
          <v-checkbox
            v-model="moldeEnCursoDraft"
            hide-details
            class="mt-0"
            label="Mostrar molde en curso"
          />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Agrega al pie de la tarjeta con qué molde está produciendo la máquina en este momento.
            Solo aparece si tiene una operación abierta y no está en paro. La leyenda
            <b>Maq. Ocupada</b> del detalle no depende de esta opción.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="restablecerConfig">Restablecer</v-btn>
          <v-spacer />
          <v-btn text @click="dialogConfig = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="guardarConfig">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import CardCambioMolde from './CardCambioMolde.vue';

// El back serializa en camelCase, pero se tolera PascalCase por si cambia el serializador.
const prop = (obj, camel) => {
  if (!obj) return undefined;
  if (obj[camel] !== undefined) return obj[camel];
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  return obj[pascal];
};

// Resuelve la llave sin distinguir mayúsculas. El estado en tiempo real serializa algunos
// acrónimos a medias ("ODTs" sale como "odTs"), así que probar camel/Pascal no alcanza.
const ciVal = (obj, nombre) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const objetivo = String(nombre).toLowerCase();
  const llave = Object.keys(obj).find(k => k.toLowerCase() === objetivo);
  return llave === undefined ? undefined : obj[llave];
};

// Las fechas del tablero llegan en ISO local sin zona ("yyyy-MM-ddTHH:mm:ss").
const toMs = (iso) => {
  if (!iso) return NaN;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? NaN : t;
};

export default {
  name: 'ReporteCambioMolde',
  components: { CardCambioMolde },

  props: {
    // Verdadero cuando la pestaña de este reporte es la visible. Todos los reportes se montan
    // a la vez en Reportes.vue, así que la carga se dispara desde aquí y no en mounted().
    active: { type: Boolean, default: false },
    // Recarga automática. Solo corre mientras la pestaña está visible.
    pollMs: { type: Number, default: 120000 },
  },

  data: () => ({
    isFullscreen: false,
    loading: false,
    error: false,
    // Respuesta cruda de /ReportesMoldes/TableroProduccionV4 (el mismo EP que alimenta la
    // gráfica de ReportePlanDeProduccionV4).
    tablero: null,
    idArea: null,
    areas: [],
    // Instancias con su área, de /MaquinaInstancias/Basico. El tablero no expone el área de la
    // máquina (solo la de la OT), así que el agrupado se resuelve con este catálogo.
    instancias: [],
    // Estado en tiempo real por instancia (/OEEV2/EstadoMaquinasTiempoReal). Da el molde que
    // está montado AHORA, sin depender del periodo consultado.
    estadoTiempoReal: [],
    intervalRecarga: null,
    // Segundos que faltan para la próxima recarga automática.
    restanteSeg: 0,
    // Arranque de la jornada, de CAT_Horarios ({ diaSemana, hh, mm }). null = aún sin cargar.
    horaJornada: null,
    // Ventana con la que se consultó el tablero, para mostrarla en la barra de filtros.
    ventanaActual: null,

    // Detalle: la tarjeta en un diálogo, con el tiempo de retraso.
    dialogDetalle: false,
    maquinaDetalle: null,

    // Configuración del reporte (ReportesConfig, código r108). Solo editable en gamemode.
    // limitar/limite: recorta cada área a las primeras N tarjetas (las de cambio más próximo).
    // periodo: 'semana' (default) o 'dia' (solo los cambios de la jornada en curso).
    // moldeEnCurso: muestra con qué molde está produciendo la máquina en este momento.
    codigoReporte: 'r108',
    configReporte: { limitar: true, limite: 5, periodo: 'semana', moldeEnCurso: false },
    limitarDraft: true,
    limiteDraft: 5,
    periodoDraft: 'semana',
    moldeEnCursoDraft: false,
    dialogConfig: false,
  }),

  watch: {
    active(visible) {
      if (visible) {
        this.cargar();
        this.iniciarAutoRecarga();
      } else {
        this.detenerAutoRecarga();
      }
    },

    idArea(nuevo, previo) {
      // El área filtra en el back (idsArea). -1/null son ambos "sin área": se normalizan para
      // evitar el doble disparo cuando Vuetify convierte el valor inicial al montar.
      const norm = v => (v == null ? -1 : Number(v));
      if (norm(nuevo) === norm(previo)) return;
      this.cargar();
    },
  },

  computed: {
    fsLabel() { return this.isFullscreen ? 'Salir de Pantalla' : 'Extender Pantalla'; },
    fsIcon()  { return this.isFullscreen ? 'mdi-arrow-collapse-all' : 'mdi-arrow-expand-all'; },

    // Modo desarrollador: habilita el botón/diálogo de configuración del reporte.
    gamemode() {
      return localStorage.getItem('gamemode') === 'true';
    },

    periodoLabel() {
      return (this.configReporte || {}).periodo === 'dia' ? 'Día' : 'Semana';
    },

    periodoOpciones() {
      return [
        { text: 'Semana completa', value: 'semana' },
        { text: 'Solo el día en curso', value: 'dia' },
      ];
    },

    // Rango consultado, para que se vea con qué filtro de tiempo está armado el reporte.
    ventanaTxt() {
      const v = this.ventanaActual;
      if (!v) return '';
      const f = d => moment(d).format('DD/MM HH:mm');
      return `${f(v.inicio)} → ${f(v.fin)}`;
    },

    // Cuenta regresiva de la recarga automática, en m:ss.
    restanteTxt() {
      const total = Math.max(0, Number(this.restanteSeg) || 0);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },

    // "Ahora" según el backend. Es la misma referencia que usa el tablero para proyectar el plan.
    ahoraMs() {
      const t = toMs(prop(this.tablero, 'fechaConsulta'));
      return Number.isNaN(t) ? Date.now() : t;
    },

    // idMaquinaIns -> { molde, activa, ocupada }: qué molde está montado ahora y en qué estado
    // está la máquina. "Activa" = tiene operación abierta y no tiene paro abierto, el mismo
    // criterio de status 1 del reporte de estado de máquinas. "Ocupada" solo mira la operación
    // abierta: aunque esté en paro, el molde sigue montado y la máquina no está disponible para
    // el cambio.
    estadoPorInstancia() {
      const mapa = new Map();

      (this.estadoTiempoReal || []).forEach((estado) => {
        const id = Number(ciVal(estado, 'IdMaquinaIns'));
        if (!id) return;

        const moldes = ciVal(estado, 'Moldes');
        const molde = Array.isArray(moldes) && moldes.length
          ? (ciVal(moldes[0], 'NombreMolde') || '')
          : '';

        const enParo = ciVal(estado, 'EnParoNoProgramado') === true
          || ciVal(estado, 'EnParoProgramado') === true
          || ciVal(estado, 'TieneParoAbierto') === true;
        const ocupada = ciVal(estado, 'TieneOperacionAbierta') === true;
        const activa = !enParo && ocupada;

        mapa.set(id, { molde, activa, ocupada });
      });

      return mapa;
    },

    // idMaquinaIns -> área de la instancia (nombre). Es el área real de la máquina.
    areaPorInstancia() {
      const mapa = new Map();
      (this.instancias || []).forEach((ins) => {
        const id = Number(prop(ins, 'idMaquinaIns'));
        if (!id) return;
        mapa.set(id, prop(ins, 'area') || '');
      });
      return mapa;
    },

    // Un cambio de molde por cada OT NO ARRANCADA cuyo inicio teórico cae en el periodo. Cada OT
    // lleva un molde, así que cada OT pendiente es un cambio por anunciar. El recorte al periodo
    // ya lo hizo el backend (filtra por FechaInicioDeProduccion entre inicio y fin), así que aquí
    // solo se descartan las que ya arrancaron.
    cambios() {
      const delTablero = (this.tablero && prop(this.tablero, 'maquinas')) || [];
      const filas = [];

      delTablero.forEach((maq) => {
        const idMaquinaIns = Number(prop(maq, 'idInstancia') || prop(maq, 'idMaquina')) || null;
        const codigoMaquina = prop(maq, 'nombreMaquina') || '';

        (prop(maq, 'barrasTeoricas') || []).forEach((barra) => {
          // Solo pendientes: si ya arrancó, su cambio de molde ya ocurrió. La finalizada se
          // descarta por seguridad (no debería llegar sin haber arrancado).
          if (prop(barra, 'iniciada') || prop(barra, 'finalizada')) return;

          const inicioMs = toMs(prop(barra, 'inicio'));

          filas.push({
            idMaquinaIns,
            idTrabajoOrden: prop(barra, 'idTrabajoOrden'),
            codigoMaquina,
            // Área de la instancia. Si el catálogo aún no cargó, se cae al área de la OT.
            area: this.areaPorInstancia.get(idMaquinaIns) || prop(barra, 'area') || 'S/D',

            moldeSiguiente:  prop(barra, 'molde') || '',
            claveSiguiente:  prop(barra, 'clave') || '',
            inicioSiguiente: prop(barra, 'inicio') || null,
            // OT crítica (TrabajoOrden.OTCritica): la tarjeta se rodea de rojo, igual que en V4.
            otCritica: prop(barra, 'otCritica') === true,
            // Artículos con sus cavidades y su meta de piezas.
            cavidadesArticulos: this.cavidadesPorArticulo(barra),
            // Piezas programadas de toda la OT (suma de las cantidades de sus operaciones).
            piezasProgramadas: Number(prop(barra, 'piezasObjetivo')) || 0,
            // Duración teórica de la OT, SIN la holgura: el tiempo de la operación más larga a su
            // Tc. La holgura (el colchón que el plan agrega para cuadrar el arranque siguiente)
            // queda fuera a propósito.
            duracionSegundos: Number(prop(barra, 'duracionTeoricaSegundos')) || 0,

            // Molde con el que la máquina está produciendo AHORA. Vacío si no está activa.
            moldeEnCurso: this.moldeActivoDe(idMaquinaIns),
            // La máquina trae una OT en curso: su molde está montado, así que no está disponible
            // para el cambio. Cuarto criterio de orden y leyenda "Maq. Ocupada" en el detalle.
            maqOcupada: this.ocupadaDe(idMaquinaIns),

            // Claves de orden: si tiene hora programada y cuál es. Sin fecha válida, al final.
            programada: !Number.isNaN(inicioMs),
            inicioMs: Number.isNaN(inicioMs) ? Number.MAX_SAFE_INTEGER : inicioMs,
          });
        });
      });

      return filas;
    },

    // Agrupado por área. Las áreas van por nombre; dentro de cada área los cambios van por fecha
    // y hora: el más próximo a la izquierda y los siguientes hacia la derecha.
    grupos() {
      const porArea = new Map();

      this.cambios.forEach((m) => {
        if (!porArea.has(m.area)) porArea.set(m.area, []);
        porArea.get(m.area).push(m);
      });

      const porNombre = (a, b) => String(a || '').localeCompare(String(b || ''), 'es', { numeric: true });

      // Cuatro criterios, en este orden:
      //   1. Crítica: los moldes críticos van siempre al inicio.
      //   2. Programada: primero las que tienen hora, al final las que no.
      //   3. Hora programada: la más próxima primero.
      //   4. Disponibilidad de la máquina: con el molde montado (OT en curso) va después, porque
      //      el cambio no puede arrancar hasta que libere.
      // El nombre de la máquina cierra el desempate para que el orden sea estable.
      const porUrgencia = (a, b) => {
        if (a.otCritica !== b.otCritica) return a.otCritica ? -1 : 1;
        if (a.programada !== b.programada) return a.programada ? -1 : 1;
        if (a.inicioMs !== b.inicioMs) return a.inicioMs - b.inicioMs;
        if (a.maqOcupada !== b.maqOcupada) return a.maqOcupada ? 1 : -1;
        return porNombre(a.codigoMaquina, b.codigoMaquina);
      };

      // Con un área seleccionada se ven todos sus cambios: el límite es para la vista general,
      // donde conviven todas las áreas.
      const cfg = this.configReporte || {};
      const limite = (cfg.limitar && !(this.idArea > 1))
        ? Math.max(1, Number(cfg.limite) || 1)
        : null;

      return [...porArea.entries()]
        .sort((a, b) => porNombre(a[0], b[0]))
        .map(([area, lista]) => {
          const ordenadas = [...lista].sort(porUrgencia);
          return {
            area,
            cambios: limite ? ordenadas.slice(0, limite) : ordenadas,
          };
        });
    },
  },

  async mounted() {
    // La config define el rango que se le pide al backend, así que se resuelve antes de cargar.
    await this.cargarConfig();
    this.getAreas();
    this.getInstancias();
    if (this.active) {
      this.cargar();
      this.iniciarAutoRecarga();
    }

    const sync = () => {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    document.addEventListener('MSFullscreenChange', sync);
    this._syncFs = sync;
  },

  beforeDestroy() {
    this.detenerAutoRecarga();
    if (this._syncFs) {
      document.removeEventListener('fullscreenchange', this._syncFs);
      document.removeEventListener('webkitfullscreenchange', this._syncFs);
      document.removeEventListener('MSFullscreenChange', this._syncFs);
    }
  },

  methods: {
    async extenderPantalla() {
      const el = this.$refs.fsRoot;
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        this.isFullscreen = true;
      } else {
        await document.exitFullscreen?.();
        this.isFullscreen = false;
      }
    },

    getAreas() {
      axios.get('/Areas')
        .then((r) => {
          const cmp = (a, b) => String(a || '').localeCompare(String(b || ''), 'es', { numeric: true });
          // idArea > 1 descarta "SIN DESIGNAR" (IdArea = 1).
          this.areas = (r.data || [])
            .filter(a => a.idArea > 1)
            .sort((a, b) => cmp(a.nombre, b.nombre));
        })
        .catch(e => console.error('Error cargando áreas', e));
    },

    // Estado en tiempo real de las máquinas: de aquí sale el molde que está montado ahora y si la
    // máquina está ocupada. Mismo endpoint que alimenta el reporte de estado de máquinas. Se pide
    // siempre: la disponibilidad es el cuarto criterio de orden, no un dato opcional.
    async getEstadoTiempoReal() {
      try {
        const p = new URLSearchParams();
        if (this.idArea > 1) p.append('idsAreas', this.idArea);
        const qs = p.toString();
        const { data } = await axios.get(`/OEEV2/EstadoMaquinasTiempoReal${qs ? `?${qs}` : ''}`);
        this.estadoTiempoReal = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('Error cargando el estado de máquinas en tiempo real', e);
      }
    },

    // Catálogo de instancias con su área. Es la fuente del agrupado por área, porque el tablero
    // solo trae el área de la OT (la de su primera operación), no la de la máquina.
    getInstancias() {
      axios.get('/MaquinaInstancias/Basico')
        .then((r) => {
          const cmp = (a, b) => String(a || '').localeCompare(String(b || ''), 'es', { numeric: true });
          this.instancias = (r.data || []).sort((a, b) => cmp(a.nombre, b.nombre));
        })
        .catch(e => console.error('Error cargando instancias', e));
    },

    /* ────────────────────────────────────────
       DETALLE
    ──────────────────────────────────────── */
    abrirDetalle(maquina) {
      this.maquinaDetalle = maquina;
      this.dialogDetalle = true;
    },

    /* ────────────────────────────────────────
       CONFIGURACIÓN DEL REPORTE (r108)
    ──────────────────────────────────────── */
    abrirConfig() {
      const cfg = this.configReporte || {};
      this.limitarDraft = cfg.limitar !== false;
      this.limiteDraft = Number(cfg.limite) || 5;
      this.periodoDraft = cfg.periodo === 'dia' ? 'dia' : 'semana';
      this.moldeEnCursoDraft = cfg.moldeEnCurso === true;
      this.dialogConfig = true;
    },

    getDefaultConfig() {
      return { limitar: true, limite: 5, periodo: 'semana', moldeEnCurso: false };
    },

    normalizarConfig(cfg = {}) {
      const source = cfg && typeof cfg === 'object' ? cfg : {};
      const limitar = source.limitar !== false;
      let limite = Number(source.limite);
      if (!Number.isFinite(limite)) limite = 5;
      limite = Math.round(Math.min(50, Math.max(1, limite)));
      const periodo = source.periodo === 'dia' ? 'dia' : 'semana';
      const moldeEnCurso = source.moldeEnCurso === true;
      return { limitar, limite, periodo, moldeEnCurso };
    },

    // La preferencia puede llegar como string JSON, como { configJson } (camel) o { ConfigJson } (pascal).
    extraerConfig(pref = null) {
      if (pref == null) return {};
      if (typeof pref === 'string') {
        try { return JSON.parse(pref); } catch (e) { return {}; }
      }
      const rawConfigJson = pref.configJson ?? pref.ConfigJson;
      if (typeof rawConfigJson === 'string') {
        try { return JSON.parse(rawConfigJson); } catch (e) { return {}; }
      }
      return pref;
    },

    async cargarConfig() {
      // Cache local para pintar de inmediato sin esperar al backend.
      try {
        const local = localStorage.getItem(this.codigoReporte);
        if (local) this.configReporte = this.normalizarConfig(this.extraerConfig(JSON.parse(local)));
      } catch (e) { /* ignore */ }
      try {
        const { data } = await axios.get(`ReportesConfig/Obtener/${this.codigoReporte}`);
        const pref = this.normalizarConfig(this.extraerConfig(data));
        this.configReporte = pref;
        localStorage.setItem(this.codigoReporte, JSON.stringify(pref));
      } catch (e) {
        // 404 = sin config guardada aún: se usa el default. No se persiste solo por leer.
        if (!localStorage.getItem(this.codigoReporte)) this.configReporte = this.getDefaultConfig();
      }
    },

    async persistirPreferencias(cfg) {
      const pref = this.normalizarConfig(this.extraerConfig(cfg));
      localStorage.setItem(this.codigoReporte, JSON.stringify(pref));
      try {
        await axios.post('ReportesConfig/Guardar/', {
          codigoReporte: this.codigoReporte,
          configJson: JSON.stringify(pref),
        });
      } catch (e) { console.log(e); }
    },

    async guardarConfig() {
      const periodoPrevio = (this.configReporte || {}).periodo;
      this.configReporte = this.normalizarConfig({
        limitar: this.limitarDraft,
        limite: this.limiteDraft,
        periodo: this.periodoDraft,
        moldeEnCurso: this.moldeEnCursoDraft,
      });
      await this.persistirPreferencias(this.configReporte);
      this.dialogConfig = false;
      // El periodo cambia el rango que se le pide al backend: hay que volver a consultar.
      if (this.configReporte.periodo !== periodoPrevio) this.cargar();
    },

    async restablecerConfig() {
      const periodoPrevio = (this.configReporte || {}).periodo;
      this.limitarDraft = true;
      this.limiteDraft = 5;
      this.periodoDraft = 'semana';
      this.moldeEnCursoDraft = false;
      this.configReporte = this.getDefaultConfig();
      await this.persistirPreferencias(this.configReporte);
      if (this.configReporte.periodo !== periodoPrevio) this.cargar();
    },

    // Molde que la máquina está corriendo ahora. Cadena vacía si la opción está apagada, si no
    // está activa o si no se conoce su estado. Se muestra aunque coincida con el que entra: saber
    // que ya lo trae montado también dice algo.
    moldeActivoDe(idMaquinaIns) {
      if (!(this.configReporte || {}).moldeEnCurso) return '';
      const estado = this.estadoPorInstancia.get(idMaquinaIns);
      if (!estado || !estado.activa || !estado.molde) return '';
      return estado.molde;
    },

    // La máquina tiene una OT en curso (operación abierta), esté o no en paro: el molde sigue
    // montado. A diferencia del molde en curso, esto no depende de la configuración porque entra
    // en el orden de las tarjetas.
    ocupadaDe(idMaquinaIns) {
      const estado = this.estadoPorInstancia.get(idMaquinaIns);
      return !!(estado && estado.ocupada);
    },

    // Artículos con sus cavidades y su meta, mismo criterio que el reporte de estado de máquinas:
    // cavidades = cuántas operaciones de la OT producen ese artículo; meta = la suma de las
    // cantidades de esas operaciones.
    cavidadesPorArticulo(barra) {
      const ops = prop(barra, 'operaciones') || [];
      const mapa = new Map();

      ops.forEach((op) => {
        const nombre = prop(op, 'codigo') || prop(op, 'nombre');
        if (!nombre) return;
        const acumulado = mapa.get(nombre) || { nombre, cavidades: 0, meta: 0 };
        acumulado.cavidades += 1;
        acumulado.meta += Number(prop(op, 'cantidad')) || 0;
        mapa.set(nombre, acumulado);
      });

      return [...mapa.values()];
    },

    /* ────────────────────────────────────────
       JORNADA DEL DÍA
    ──────────────────────────────────────── */
    // Hora de arranque de la jornada, igual que la calcula V4 para su rango por defecto: del
    // catálogo de horarios activos se toma el día con menor diaSemana y se usa su hora de inicio.
    async cargarHorario() {
      try {
        // /Horarios responde { events, start, intervals }: los horarios están en events.
        const { data } = await axios.get('/Horarios');
        const horarios = Array.isArray(data)
          ? data
          : (Array.isArray(data && data.events) ? data.events : []);

        if (!horarios.length) {
          this.horaJornada = { hh: 0, mm: 0 };
          return;
        }

        const menor = horarios.reduce((min, h) =>
          Number(prop(h, 'diaSemana')) < Number(prop(min, 'diaSemana')) ? h : min,
        );
        const [hh, mm] = String(prop(menor, 'inicio') || '00:00').split(':').map(Number);
        this.horaJornada = {
          diaSemana: Number(prop(menor, 'diaSemana')) || 0,
          hh: hh || 0,
          mm: mm || 0,
        };
      } catch (e) {
        // Sin horarios se cae al lunes a medianoche, que es el default del backend.
        this.horaJornada = { diaSemana: 1, hh: 0, mm: 0 };
        console.error('Error cargando horarios', e);
      }
    },

    // Ventana de la jornada en curso: arranca a la hora del catálogo y dura 24 h. Si todavía no
    // dan esa hora, seguimos dentro de la jornada que arrancó ayer (a las 05:00 con jornada de
    // 06:00, el día va de las 06:00 de ayer a las 06:00 de hoy).
    ventanaDia() {
      const { hh, mm } = this.horaJornada || { hh: 0, mm: 0 };
      const ahora = new Date();
      const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), hh, mm, 0, 0);
      if (ahora < inicio) inicio.setDate(inicio.getDate() - 1);
      const fin = new Date(inicio);
      fin.setDate(fin.getDate() + 1);
      return { inicio, fin };
    },

    // Ventana de la semana, igual que el rango por defecto de V4: última ocurrencia del día de
    // arranque (0=domingo … 6=sábado) a su hora, más 7 días.
    ventanaSemana() {
      const { diaSemana, hh, mm } = this.horaJornada || { diaSemana: 1, hh: 0, mm: 0 };
      const hoy = new Date();
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
      const diff = (inicio.getDay() - diaSemana + 7) % 7;
      inicio.setDate(inicio.getDate() - diff);
      inicio.setHours(hh || 0, mm || 0, 0, 0);
      const fin = new Date(inicio);
      fin.setDate(inicio.getDate() + 7);
      return { inicio, fin };
    },

    ventanaPeriodo() {
      const cfg = this.configReporte || {};
      return cfg.periodo === 'dia' ? this.ventanaDia() : this.ventanaSemana();
    },

    fmtIso(d) {
      const p = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
        + `T${p(d.getHours())}:${p(d.getMinutes())}:00`;
    },

    reiniciarContador() {
      this.restanteSeg = Math.max(1, Math.round(this.pollMs / 1000));
    },

    // Tick de 1 s: descuenta la cuenta regresiva y dispara la recarga al llegar a 0. No descuenta
    // mientras hay una carga en curso, para no encimar peticiones si el tablero tarda de más.
    iniciarAutoRecarga() {
      this.detenerAutoRecarga();
      this.reiniciarContador();
      this.intervalRecarga = setInterval(() => {
        if (this.loading) return;
        if (this.restanteSeg <= 1) this.cargar();
        else this.restanteSeg -= 1;
      }, 1000);
    },

    detenerAutoRecarga() {
      if (!this.intervalRecarga) return;
      clearInterval(this.intervalRecarga);
      this.intervalRecarga = null;
    },

    recargarManual() {
      if (this.loading) return;
      this.cargar();
    },

    // El periodo es la jornada del día, no la semana: se acota con inicio/fin. Sin ellos el back
    // devolvería lunes→domingo.
    async cargar() {
      // Toda recarga, manual o automática, reinicia la cuenta regresiva.
      this.reiniciarContador();
      this.loading = true;
      this.error = false;
      try {
        // La hora de arranque se resuelve una sola vez, antes de la primera consulta.
        if (!this.horaJornada) await this.cargarHorario();
        const { inicio, fin } = this.ventanaPeriodo();
        this.ventanaActual = { inicio, fin };

        const p = new URLSearchParams();
        p.append('inicio', this.fmtIso(inicio));
        p.append('fin', this.fmtIso(fin));
        if (this.idArea > 1) p.append('idsArea', this.idArea);

        const { data } = await axios.get(`/ReportesMoldes/TableroProduccionV4?${p.toString()}`);
        this.tablero = data;
      } catch (e) {
        this.error = true;
        console.error('Error cargando el tablero de cambio de molde', e);
      } finally {
        this.loading = false;
      }
      // El estado real es tiempo real y no depende del periodo: se pide aparte y si falla no
      // invalida el tablero, solo se pierde el aviso del molde en curso.
      this.getEstadoTiempoReal();
    },
  },
};
</script>

<style scoped>
.cm-root {
  --cm-border: rgba(0, 0, 0, 0.07);
  --cm-font: 'Space Grotesk', 'Roboto', sans-serif;
  background: #F4F6F9;
}

.cm-btn {
  flex: 0 0 auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: #fff;
  font-family: var(--cm-font);
  font-size: 12px;
  font-weight: 600;
  color: #3d4a5c;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  white-space: nowrap;
  outline: none;
}

.cm-btn:hover {
  background: #f4f6f9;
  border-color: rgba(0, 0, 0, 0.25);
}

.cm-btn .v-icon {
  color: #5A6476 !important;
}

.cm-board.v-card {
  background: transparent !important;
  border: none !important;
}

.cm-board-body.v-card__text {
  padding: 10px 12px 16px !important;
}

/* Filtro de área */
.cm-area-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

/* En pantalla completa el botón de salida queda solo: se alinea a la derecha. */
.cm-btn--solo {
  margin-left: auto;
}

.cm-area-select.v-input {
  background: #fff;
  border-radius: 10px;
  font-family: var(--cm-font) !important;
  font-size: 13px !important;
  flex: 1 1 auto;
}

/* Rango de tiempo con el que se consultó el tablero. */
.cm-periodo {
  flex: 0 0 auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 11px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--cm-border);
  font-family: var(--cm-font);
  font-size: 11.5px;
  color: #5A6476;
  white-space: nowrap;
}

.cm-periodo b {
  font-weight: 700;
  color: #0D1117;
}

.cm-periodo-rango {
  color: #8A95A3;
}

.cm-periodo .v-icon {
  color: #8A95A3 !important;
}

.cm-cfg-lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5A6476;
  margin-bottom: 6px;
}

/* Cuenta regresiva de la recarga automática. */
.cm-refresh {
  flex: 0 0 auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: var(--cm-font);
  font-size: 11.5px;
  font-weight: 600;
  color: #5A6476;
  white-space: nowrap;
}

.cm-refresh .v-icon {
  color: #8A95A3 !important;
}

.cm-reload-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--cm-border);
  background: #fff;
  color: #5A6476;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  outline: none;
}

.cm-reload-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cm-reload-btn .v-icon {
  color: #5A6476 !important;
}

.cm-reload-spin {
  animation: cm-reload-spin 0.9s linear infinite;
}

@keyframes cm-reload-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ===================================================
   FILA POR ÁREA
   =================================================== */
.cm-area-grupo + .cm-area-grupo {
  margin-top: 12px;
}

.cm-area-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding-bottom: 3px;
  margin-bottom: 7px;
  border-bottom: 1px solid var(--cm-border);
}

.cm-area-nombre {
  font-family: var(--cm-font);
  font-size: 15px;
  font-weight: 700;
  color: #2B7EFF;
  letter-spacing: -0.01em;
}

.cm-area-info.v-icon {
  /* El gap de 10px de la fila es para bloques, no para el icono: se acerca al nombre del área. */
  margin-left: -4px;
  color: #B4BCC7 !important;
  cursor: help;
  align-self: center;
}

.cm-area-info.v-icon:hover {
  color: #2B7EFF !important;
}

/* Las tarjetas de un área van en una fila que envuelve; el ancho fijo mantiene la lectura
   uniforme cuando un área tiene muchas máquinas. */
.cm-area-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cm-area-cards > * {
  flex: 0 1 226px;
  min-width: 200px;
  max-width: 260px;
}

/* Estados vacíos */
.cm-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
}

.cm-loading-text,
.cm-empty {
  font-family: var(--cm-font);
  font-size: 13px;
  color: #8A95A3;
}

.cm-empty {
  padding: 60px 0;
  text-align: center;
}

/* Pantalla completa. Se usa la clase (flag JS) porque :fullscreen no aplica de forma fiable. */
.cm-root:fullscreen,
.cm-root:-webkit-full-screen,
.cm-root.cm-root--fs {
  padding: 14px 20px;
  height: 100vh !important;
  width: 100vw;
  background: #F4F6F9;
  overflow-y: auto !important;
  overflow-x: hidden;
}

@media (max-width: 600px) {
  .cm-board-body.v-card__text { padding: 12px 6px 16px !important; }
  .cm-btn-label { display: none; }
  .cm-btn { padding: 0 10px; }
}
</style>

<!-- Sin scoped: el contenido del diálogo se monta fuera de este componente (portal de Vuetify),
     así que una regla scoped no lo alcanzaría. La tarjeta ya trae su propio fondo y borde, por
     eso el contenedor del diálogo se deja transparente. -->
<style>
.cm-detalle-dialog {
  box-shadow: none !important;
  background: transparent !important;
}

/* Tooltip con los criterios de orden. Igual que el diálogo, se monta fuera del componente.
   Fondo opaco: el default de Vuetify es translúcido y encima de las tarjetas no se leía. */
.cm-orden-tip.v-tooltip__content {
  font-family: 'Space Grotesk', 'Roboto', sans-serif;
  font-size: 12.5px;
  line-height: 1.4;
  /* El texto del tooltip viene centrado por default: una lista se lee alineada a la izquierda. */
  text-align: left;
  color: #fff;
  opacity: 1 !important;
  background: #0D1117 !important;
  border-radius: 10px;
  padding: 11px 14px !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
}

.cm-orden-tit {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8FB6FF;
  margin-bottom: 7px;
}

.cm-orden-lista {
  margin: 0;
  padding-left: 17px;
}

.cm-orden-lista li {
  color: #E6EAF0;
}

.cm-orden-lista li b {
  color: #fff;
  font-weight: 700;
}

.cm-orden-lista li + li {
  margin-top: 5px;
}
</style>
